/**
 * Trailing-edge debounce: `fn` runs once, `ms` after the LAST call.
 *
 * Used by the Goals Inventory search box — every keystroke used to trigger a
 * full innerHTML rebuild of ~330 goals (plus SortableJS drop zones in Manage
 * Hierarchy mode). Debounced, a typed word costs one rebuild.
 */
export interface Debounced<A extends unknown[]> {
  (...args: A): void;
  /** Run a pending call now (no-op if none is pending). */
  flush(): void;
  /** Drop a pending call. */
  cancel(): void;
}

export function debounce<A extends unknown[]>(fn: (...args: A) => void, ms: number): Debounced<A> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: A | null = null;
  const run = () => {
    timer = null;
    const args = lastArgs as A;
    lastArgs = null;
    fn(...args);
  };
  const d = ((...args: A) => {
    lastArgs = args;
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(run, ms);
  }) as Debounced<A>;
  d.flush = () => {
    if (timer === null) return;
    clearTimeout(timer);
    run();
  };
  d.cancel = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    lastArgs = null;
  };
  return d;
}

/**
 * The catalogue search term as `renderCatalogue` reads it. Shared so the
 * input handler can skip a rebuild when the effective query is unchanged
 * (e.g. a trailing space, or a character typed and deleted inside the
 * debounce window).
 */
export function normaliseCatalogueQuery(raw: string): string {
  return raw.toLowerCase().trim();
}
